import React from "react";
import { Button } from "@/components/ui/button";
import { Info, FileEdit, Trash2, PackageX, Maximize2, Archive, Weight } from "lucide-react";
import { Package } from "@/types";
import StatusBadge, { statusColorMap } from "@/components/StatusBadge";
import PackageTypeIcon from "./PackageTypeIcon";
import { isPackageOlderThan30Days } from "@/lib/utils";
import StatusTimeline from "@/components/shared/StatusTimeline";
import { formatDistanceToNow } from "date-fns";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { PackageGridSkeleton } from "./PackageTableSkeleton";

interface GridViewProps {
  packages: Package[];
  isLoading: boolean;
  onMarkDelivered: (pkg: Package) => void;
  onUndoDelivery: (pkg: Package) => void;
  onViewInfo: (pkg: Package) => void;
  onViewHistory: (pkg: Package) => void;
  onDelete: (pkg: Package) => void;
  onDiscard: (pkg: Package) => void;
  onToggleObservation: (pkgId: number, observation: string) => void;
  onUndoDelete: (id: number) => void;
  onUndoDiscard: (id: number) => void;
  onEdit: (pkg: Package) => void;
}

const PackageTableGridView: React.FC<GridViewProps> = ({
  packages,
  isLoading,
  onMarkDelivered,
  onUndoDelivery,
  onViewInfo,
  onViewHistory,
  onDelete,
  onDiscard,
  onToggleObservation,
  onUndoDelete,
  onUndoDiscard,
  onEdit,
}) => {
  if (isLoading) {
    return <PackageGridSkeleton />;
  }

  if (packages.length === 0) {
    return (
      <div className="text-center py-12 col-span-1 sm:col-span-2 lg:col-span-3">
        <h3 className="text-lg font-medium">No Packages Found</h3>
        <p className="text-sm text-muted-foreground mt-2">
          There are currently no packages matching your filters.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...packages].sort((a, b) => b.sequential_number - a.sequential_number).map((pkg) => (
        <div
          key={pkg.id}
          className={
            `relative border rounded-lg p-3 flex flex-col justify-between min-h-[320px] bg-white transition-shadow duration-150 ` +
            `hover:shadow-lg hover:ring-2 hover:ring-primary/20`}
          style={{ boxShadow: "0 1px 4px 0 rgba(0,0,0,0.04)", borderLeft: `4px solid ${statusColorMap[pkg.status]}` }}
        >
          {/* Subtle Package ID in top-left */}
          <div className="absolute left-3 top-3 text-xs text-muted-foreground font-mono opacity-70 select-none">
            #{String(pkg.sequential_number).padStart(4, '0')}
          </div>
          {/* Info button in top-right */}
          <div className="absolute right-3 top-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewInfo(pkg)}
                    className="h-7 w-7 text-blue-500 hover:text-blue-600"
                  >
                    <Info size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Package Info</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {/* Card Content */}
          <div className="flex-1 flex flex-col justify-between pt-8">
            {/* Guest Name */}
            <div className="font-semibold text-lg mb-1 truncate">{pkg.guest_name}</div>
            {/* Status, Entry Date, Unit # on one row */}
            <div className="flex flex-row items-center gap-2 text-xs text-muted-foreground mb-2">
              <StatusBadge status={pkg.status} />
              <span className="mx-1">•</span>
              <span>{pkg.room_number || 'N/A'}</span>
              <span className="mx-1">•</span>
              <span>{pkg.entry_date ? formatDistanceToNow(new Date(pkg.entry_date), { addSuffix: true }) : ''}</span>
            </div>
            {/* Tracking Number */}
            <div className="text-xs text-muted-foreground mb-1">Tracking: <span className="font-mono text-gray-700">{pkg.tracking}</span></div>
            {/* Signature */}
            {pkg.signature ? (
              <div className="flex items-center gap-2 mt-2">
                <img src={pkg.signature} alt="Signature" className="w-20 h-12 object-contain rounded shadow border bg-white" />
                <span className="text-xs text-muted-foreground">Signature</span>
              </div>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-gray-400 italic cursor-help mt-2">No signature</span>
                  </TooltipTrigger>
                  <TooltipContent>No signature collected</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {/* Observations */}
            <div className="flex gap-2 mt-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`p-2 rounded-md border ${pkg.observations.oversized ? "bg-primary/20 border-primary" : "bg-white border-gray-300"}`}
                      onClick={() => onToggleObservation(pkg.id, "oversized")}
                      aria-label="Toggle Oversized"
                      style={{ minWidth: 0 }}
                    >
                      <Maximize2 size={18} className={pkg.observations.oversized ? "text-green-600" : "text-gray-400"} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Oversized</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`p-2 rounded-md border ${pkg.observations.overweight ? "bg-primary/20 border-primary" : "bg-white border-gray-300"}`}
                      onClick={() => onToggleObservation(pkg.id, "overweight")}
                      aria-label="Toggle Overweight"
                      style={{ minWidth: 0 }}
                    >
                      <Weight size={18} className={pkg.observations.overweight ? "text-green-600" : "text-gray-400"} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Overweight</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
          {/* Actions */}
          <div className="mt-3 space-y-2">
            {pkg.status === "Pending" ? (
              <Button
                variant="default"
                size="sm"
                className="inline-flex items-center w-full"
                onClick={() => onMarkDelivered(pkg)}
              >
                Mark Delivered
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="inline-flex items-center w-full"
                onClick={() => onUndoDelivery(pkg)}
              >
                Reverse Changes
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="w-full">Actions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => onEdit(pkg)}
                        className="flex items-center gap-2"
                      >
                        <FileEdit size={16} className="text-primary group-hover:opacity-100" />
                        Edit
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>Edit</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => onDelete(pkg)}
                        className="text-red-500"
                      >
                        <Trash2 size={16} />
                        Delete
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>Delete</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <DropdownMenuItem
                        onSelect={() => onDiscard(pkg)}
                        className={isPackageOlderThan30Days(pkg.entry_date) ? "text-orange-500" : ""}
                      >
                        <PackageX size={16} />
                        Discard
                      </DropdownMenuItem>
                    </TooltipTrigger>
                    <TooltipContent>Discard</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PackageTableGridView;
